/**
 * Solandia authorization.
 *
 * THE rule, enforced everywhere: every read/write is scoped to the caller's
 * Membership in the target `familyId`.
 *   - `parent`  → full access to every scope in their family.
 *   - `trusted` → access ONLY to the scopes listed in `membership.permissions`.
 *   - A membership grants nothing in any *other* family.
 *
 * Health, documents and messages are sensitive scopes — they are never implied
 * and must be explicitly granted to a trusted member.
 *
 * This module is pure (no I/O). The API layer loads the caller's Membership
 * for the requested family and then calls these helpers to gate the operation.
 */
import type { Membership, Scope } from './types.js';

/** Every scope, in canonical order. */
export const ALL_SCOPES: readonly Scope[] = [
  'children',
  'calendar',
  'contacts',
  'health',
  'documents',
  'messages',
  'expenses',
  'shopping',
] as const;

/** Scopes that carry sensitive (incl. GDPR Art. 9) data. */
export const SENSITIVE_SCOPES: readonly Scope[] = ['health', 'documents', 'messages'] as const;

/** Error thrown when authorization fails. The API maps this to HTTP 403. */
export class AuthorizationError extends Error {
  readonly code = 'forbidden';
  constructor(message = 'You do not have access to this resource.') {
    super(message);
    this.name = 'AuthorizationError';
  }
}

/** True when the scope is considered sensitive. */
export function isSensitiveScope(scope: Scope): boolean {
  return SENSITIVE_SCOPES.includes(scope);
}

/**
 * Is this membership usable at all? Only `active` memberships authorize access;
 * an `invited` membership has accepted nothing yet.
 */
export function isActiveMember(membership: Membership): boolean {
  return membership.status === 'active';
}

/** Does the membership belong to (and is active in) the given family? */
export function isMemberOfFamily(membership: Membership, familyId: string): boolean {
  return isActiveMember(membership) && membership.familyId === familyId;
}

/** Parents implicitly hold every scope. */
export function effectiveScopes(membership: Membership): readonly Scope[] {
  if (membership.role === 'parent') return ALL_SCOPES;
  return membership.permissions;
}

/**
 * Core check: can this membership access the given scope?
 * Requires an active membership; parents pass for every scope, trusted members
 * only for explicitly granted scopes.
 */
export function canAccess(membership: Membership, scope: Scope): boolean {
  if (!isActiveMember(membership)) return false;
  if (membership.role === 'parent') return true;
  return membership.permissions.includes(scope);
}

/**
 * Convenience: can the membership access ALL of the given scopes?
 * Useful for operations that touch more than one scope (e.g. an event that also
 * references a contact).
 */
export function canAccessAll(membership: Membership, scopes: readonly Scope[]): boolean {
  return scopes.every((s) => canAccess(membership, s));
}

/**
 * Assert that `membership` is an active member of `familyId`.
 * Throws AuthorizationError otherwise. This is the family-boundary guard that
 * must run before any per-scope check — it is what stops one family from ever
 * reading another family's data.
 */
export function assertFamilyScope(membership: Membership, familyId: string): void {
  if (!isMemberOfFamily(membership, familyId)) {
    throw new AuthorizationError('This resource belongs to a different family.');
  }
}

/**
 * Assert that `membership` may access `scope` within `familyId`.
 * Combines the family-boundary guard with the per-scope check. Call this from
 * every handler: assertScope(member, familyId, 'health').
 */
export function assertScope(membership: Membership, familyId: string, scope: Scope): void {
  assertFamilyScope(membership, familyId);
  if (!canAccess(membership, scope)) {
    throw new AuthorizationError(`Missing "${scope}" permission for this family.`);
  }
}

/**
 * Only parents may administer the family itself: rename/delete the family,
 * invite/remove members, and change roles or scopes. Trusted members cannot,
 * regardless of their granted scopes.
 */
export function canManageFamily(membership: Membership): boolean {
  return isActiveMember(membership) && membership.role === 'parent';
}

/** Assert the caller may administer the family (parents only). */
export function assertCanManageFamily(membership: Membership, familyId: string): void {
  assertFamilyScope(membership, familyId);
  if (!canManageFamily(membership)) {
    throw new AuthorizationError('Only parents can manage the family.');
  }
}

/**
 * Normalize a requested scope grant for a member.
 *  - parents always end up with the full scope set,
 *  - trusted members get a de-duplicated, canonically-ordered subset,
 *  - unknown scopes are dropped.
 */
export function normalizeScopes(role: Membership['role'], requested: readonly Scope[]): Scope[] {
  if (role === 'parent') return [...ALL_SCOPES];
  const set = new Set(requested.filter((s): s is Scope => ALL_SCOPES.includes(s)));
  return ALL_SCOPES.filter((s) => set.has(s));
}
